/**
 * Safe URL helpers.
 *
 * Used to harden every sink that assigns a (potentially user-controlled) URL
 * to `window.location.href`. Without an allowlist on the URL scheme,
 * `new URL()` happily accepts `javascript:` / `data:` / `vbscript:` URIs,
 * which leads to DOM-based XSS when later used as a navigation target.
 *
 * Only `http:` and `https:` are considered safe for navigation.
 */
const SAFE_NAVIGATION_PROTOCOLS = ['http:', 'https:'];

/**
 * Returns true if the given value is a syntactically valid URL whose
 * protocol is part of the navigation allowlist.
 * @param {unknown} value
 * @returns {boolean}
 */
export function isSafeNavigationUrl(value) {
  if (typeof value !== 'string' || value.length === 0) return false;
  try {
    const parsed = new URL(value, window.location.origin);
    return SAFE_NAVIGATION_PROTOCOLS.indexOf(parsed.protocol) !== -1;
  } catch (_) {
    return false;
  }
}

/**
 * Navigates the browser to the given URL only if it passes the safety check.
 * @param {unknown} value
 * @returns {boolean} true if navigation was performed
 */
export function safeNavigate(value) {
  if (!isSafeNavigationUrl(value)) return false;
  window.location.href = value;
  return true;
}
